import React, { useRef } from 'react';

import { SentryLink } from 'apollo-link-sentry';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { isEmpty } from 'lodash-es';

import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  FieldMergeFunction,
  InMemoryCache,
  NormalizedCacheObject,
  Reference,
} from '@apollo/client';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import {
  getMainDefinition,
  relayStylePagination,
} from '@apollo/client/utilities';
import { useAuth0 } from '@auth0/auth0-react';
import { FullStory } from '@fullstory/browser';

import { possibleTypes } from '@/generated/possibleTypes.json';
import { ANALYTICS_CACHED_QUERY_FIELDS } from '@/hooks/useInvalidateAnalyticsCache';
import Auth0Failure from '@/pages/AccessRequired/Auth0Failure';
import { graphQLUri } from '@/utils/urls';

import { getAuth0ErrorCode } from './getAuth0ErrorCode';

if (import.meta.env.DEV) {
  loadDevMessages();
  loadErrorMessages();
}

const AUTH0_REDIRECT_CODES = ['consent_required', 'login_required'];

const uploadAndHttpLink = createUploadLink({
  uri: graphQLUri,
  credentials: 'include',
});

const errorLink = onError(({ networkError }) => {
  if (networkError) {
    networkError.message =
      "We're sorry, something went wrong. Please try again later.";
  }
});

const sentryLink = new SentryLink({
  attachBreadcrumbs: { includeError: true },
});

// Surfaces GraphQL operation names in the FullStory session timeline, which
// otherwise only shows opaque POSTs to the GraphQL endpoint.
const fullStoryLink = new ApolloLink((operation, forward) => {
  const definition = getMainDefinition(operation.query);
  const operationType =
    definition.kind === 'OperationDefinition'
      ? definition.operation
      : 'operation';
  FullStory('log', {
    level: 'log',
    msg: `[graphql] ${operationType} ${operation.operationName}`,
  });
  return forward(operation);
});

const cache = new InMemoryCache({
  possibleTypes,
  typePolicies: {
    Campaign: {
      fields: {
        tags: {
          merge(_, incoming) {
            return incoming;
          },
        },
        customFields: {
          merge(_, incoming) {
            return incoming;
          },
        },
        // prefer backend/incoming changes for below fields
        // with merge false since they get changed
        experiments: {
          merge: false,
        },
        sendDatetimes: {
          merge: false,
        },
        includedAudiences: {
          merge: false,
        },
        excludedAudiences: {
          merge: false,
        },
        proofAudiences: {
          merge: false,
        },
        seedAudiences: {
          merge: false,
        },
        dynamicSubjectLines: {
          merge: false,
        },
        campaignTemplate: {
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming);
          },
        },
        sections: {
          merge(_, incoming) {
            return [...incoming];
          },
        },
        includedContactAudiences: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
    Program: {
      fields: {
        fields: {
          merge(_, incoming) {
            return incoming;
          },
        },
        includedAudiences: {
          merge: false,
        },
        excludedAudiences: {
          merge: false,
        },
      },
    },
    Creative: {
      fields: {
        subjectLines: {
          merge(_, incoming) {
            return incoming;
          },
        },
        pairedTags: {
          merge(_, incoming) {
            return incoming;
          },
        },
        pairedCreatives: {
          merge(_, incoming) {
            return incoming;
          },
        },
        prohibitedTags: {
          merge(_, incoming) {
            return incoming;
          },
        },
        prohibitedCreatives: {
          merge(_, incoming) {
            return incoming;
          },
        },
        creativeFilters: {
          merge(_ = [], incoming) {
            return [...incoming];
          },
        },
      },
    },
    Client: {
      fields: {
        fiscalCalendar: {
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming);
          },
        },
      },
    },
    CampaignAudienceGroup: {
      fields: {
        audiences: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
    DynamicSection: {
      fields: {
        experiences2: {
          merge(_, incoming) {
            return incoming;
          },
        },
        experiences2UncoveredRuleset: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
    UberTemplateContentBlock: {
      fields: {
        experiences2: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
    NotificationsSetting: {
      keyFields: ['event'],
    },
    AlertRuleWithSubscriptionsType: {
      keyFields: ['hashid'],
    },
    AvailableCreative: {
      keyFields: false,
    },
    ContactSnapshotAudienceCount: {
      fields: {
        audienceSplits: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
    OrganizationUser2: {
      fields: {
        // Several queries write the same normalized user, so keep the cached
        // list when a sparser one returns [], but let any real payload win.
        accountMembers: {
          merge(existing, incoming) {
            return isEmpty(incoming) ? existing : incoming;
          },
        },
      },
    },
    Account2: {
      fields: {
        members: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
    AmazonSESConfig: {
      fields: {
        emailTags: {
          merge(existing, incoming) {
            return incoming ?? existing;
          },
        },
      },
    },
    BrandKit: {
      fields: {
        logos: {
          merge(_, incoming) {
            return incoming;
          },
        },
        colors: {
          merge(_, incoming) {
            return incoming;
          },
        },
        typographies: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
    // Analytics metrics are value-like blobs. The Metric union leaves
    // (CountMetric, RateMetric, …) carry an `id` equal to the metric NAME
    // (e.g. "sends", "openRate"), which repeats on every row — so default
    // __typename:id normalization would collapse every row into one shared
    // entity and each row would render the LAST row's numbers. keyFields:false
    // embeds them, which lets the campaigns/creatives index queries safely use
    // a caching fetchPolicy (see the *Metrics Query field policies below).
    // The id-less wrapper/group types are already embedded; declaring them
    // keeps that explicit if the schema ever adds an id to them.
    CategoryMetric: { keyFields: false },
    CountMetric: { keyFields: false },
    CurrencyMetric: { keyFields: false },
    CurrencyRateMetric: { keyFields: false },
    FractionalCountMetric: { keyFields: false },
    RateMetric: { keyFields: false },
    ScoreMetric: { keyFields: false },
    CampaignMetric: { keyFields: false },
    CreativeMetric: { keyFields: false },
    CampaignMetrics: { keyFields: false },
    CreativeMetrics: { keyFields: false },
    GroupedCampaignsMetrics: { keyFields: false },
    GroupedCreativesMetrics: { keyFields: false },
    ExperimentMetrics: { keyFields: false },
    TreatmentMetrics: { keyFields: false },
    ExperimentContactMetrics: { keyFields: false },
    TreatmentContactMetrics: { keyFields: false },
    SegmentGroupMetrics: { keyFields: false },
    SegmentMetrics: { keyFields: false },
    SubjectLineMetrics: { keyFields: false },
    SubjectLineMetric: { keyFields: false },
    // Value blobs, not entities. ink-ql permits two datasets to share a field
    // id, so default __typename:id normalization would merge them and hand the
    // rule builder the wrong field type.
    InkDataV1QueryCatalogDataset: { keyFields: false },
    InkDataV1QueryCatalogDatasetField: { keyFields: false },
    InkDataV1QueryCatalogDatasetRelation: { keyFields: false },
    // The envelopes around that same payload, outermost first. None carries an
    // `id` today, so Apollo already stores them inline; pinning the policy keeps
    // that true if one ever gains one.
    InkDataV1QueryCatalogFetchResponse: { keyFields: false },
    InkDataV1QueryCatalog: { keyFields: false },
    InkDataV1QueryCatalogType: { keyFields: false },
    Query: {
      fields: {
        creative: {
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming);
          },
        },
        // Analytics index metrics snapshots: replace wholesale on refetch with
        // identical args. keyArgs stays default so each filter/groupBy/date
        // combo is its own cache slot (prevents cross-arg bleed); merge:false
        // silences the "cache data may be lost" warning and guarantees a clean
        // full replace. Derived from ANALYTICS_CACHED_QUERY_FIELDS so this list
        // and the settings-change eviction (useInvalidateAnalyticsCache) can't
        // drift apart.
        ...Object.fromEntries(
          ANALYTICS_CACHED_QUERY_FIELDS.map((fieldName) => [
            fieldName,
            { merge: false },
          ]),
        ),
        layoutComponents: (() => {
          const relayPolicy = relayStylePagination();
          const relayMerge = relayPolicy.merge as FieldMergeFunction;
          return {
            ...relayPolicy,
            merge(existing, incoming, options) {
              const merged = relayMerge(existing, incoming, options);
              if (!merged?.edges) {
                return merged;
              }

              const fetchedLayoutComponents = new Set<string>();
              return {
                ...merged,
                edges: merged.edges.filter((edge: { node: Reference }) => {
                  const id = options.readField<string>('id', edge.node);
                  // If new components were created and got added to the top of
                  // the list, we don't want to show them again when the same
                  // component is fetched via infinite scrolling later on.
                  if (!id || fetchedLayoutComponents.has(id)) {
                    return false;
                  }

                  fetchedLayoutComponents.add(id);
                  return true;
                }),
              };
            },
          };
        })(),
      },
    },
  },
});

const InitAppWithApollo = ({ children }: React.PropsWithChildren) => {
  const {
    getAccessTokenSilently,
    loginWithRedirect,
    isAuthenticated,
    error,
    isLoading,
  } = useAuth0();

  const apolloClientRef = useRef<ApolloClient<NormalizedCacheObject> | null>(
    null,
  );

  if (isLoading) return;

  if (error) {
    return <Auth0Failure />;
  }

  if (!isAuthenticated) {
    return !!loginWithRedirect({
      appState: {
        returnTo: `${window.location.pathname}${window.location.search}`,
      },
    });
  }

  if (!apolloClientRef.current) {
    const authLink = setContext(async (_, { headers = {}, ...context }) => {
      const { pathname } = window.location;
      const clientId = pathname.split('/')[1] ?? '';

      // Check if X-CLIENT-ID should be excluded
      const shouldExcludeClientId =
        headers['X-CLIENT-ID'] === null || clientId === 'access';

      // Remove X-CLIENT-ID from headers if it's explicitly set to null/undefined
      // or if clientId is 'access' (the url path doesn't have the clientID)
      const { 'X-CLIENT-ID': _clientId, ...headersWithoutClientId } = headers;
      const baseHeaders = shouldExcludeClientId
        ? headersWithoutClientId
        : headers;

      try {
        const accessToken = await getAccessTokenSilently();

        return {
          headers: {
            ...baseHeaders,
            ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
            ...(!shouldExcludeClientId && {
              'X-CLIENT-ID': headers['X-CLIENT-ID'] ?? clientId,
            }),
          },
          ...context,
        };
      } catch (e) {
        const errorCode = getAuth0ErrorCode(e);
        if (errorCode && AUTH0_REDIRECT_CODES.includes(errorCode)) {
          await loginWithRedirect({
            appState: {
              returnTo: `${window.location.pathname}${window.location.search}`,
            },
          });
          return;
        }
        console.error('Failed to fetch new access token', e);
      }
    });

    apolloClientRef.current = new ApolloClient({
      link: ApolloLink.from([
        sentryLink,
        fullStoryLink,
        authLink,
        errorLink,
        uploadAndHttpLink,
      ]),
      cache,
      credentials: 'include',
      connectToDevTools: true,
    });
  }

  return (
    <ApolloProvider client={apolloClientRef.current}>{children}</ApolloProvider>
  );
};

export default InitAppWithApollo;
